import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { IAbstractRecord } from '@models';
import { isEmpty } from '@utils/validationUtils';

export const initialValues = {
  person: '',
  relatedCompany: '',
  type: '',
  category: '',
  isCompany: false,
  isLocal: false,
  isRegistered: false,
  registerNumber: '',
  taxAccount: '',
  account: '',
  maxCreditLimitAllowedCurrency: '39735',
  maxCreditLimitAllowed: 0,
  SalesforceManagement_BusinessPartnerStatuses: 'NEW',
  allowAccessToAllInsured: false,
};

export const drawerSections = (
  businessPartnerId: string,
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isReinsurer = defaultValues?.type?.toLowerCase() === 'reinsurer';
  return [
    {
      inputs: {
        person: {
          name: 'person',
          title: 'Person',
          type: FormFieldType.autocomplete,
          placeholder: 'Type to search for Person',
          value: defaultValues?.person,
          required: !businessPartnerId,
          hidden: !!businessPartnerId,
        },
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          type: FormFieldType.select,
          placeholder: 'Related Company',
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.relatedCompany)
              ? defaultValues.relatedCompany
              : initialValues.relatedCompany,
          disabled: false,
          required: true,
          injectGlobalLov: {
            type: 'relatedCompanies',
            value: 'Id',
            display: 'Title',
          },
        },
        type: {
          name: 'type',
          title: 'Type',
          type: FormFieldType.select,
          placeholder: 'Type',
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.type)
              ? defaultValues.type
              : initialValues.type,
          disabled: false,
          required: true,
          selectOptions: lovs.type,
        },
        category: {
          name: 'category',
          title: 'Category',
          type: FormFieldType.select,
          placeholder: 'Category',
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.category)
              ? defaultValues.category
              : initialValues.category,
          disabled: false,
          required: false,
          selectOptions: lovs.category,
        },
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access To All Insured',
          hidden: isReinsurer,
          type: FormFieldType.switch,
          value:
            !isEmpty(defaultValues) &&
            !isEmpty(defaultValues.allowAccessToAllInsured)
              ? defaultValues.allowAccessToAllInsured
              : initialValues.allowAccessToAllInsured,
          required: false,
          disabled: false,
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          placeholder: 'Is Company',
          type: FormFieldType.switch,
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.isCompany)
              ? defaultValues.isCompany
              : initialValues.isCompany,
          required: false,
          disabled: isReinsurer,
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          placeholder: 'Is Local',
          type: FormFieldType.switch,
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.isLocal)
              ? defaultValues.isLocal
              : initialValues.isLocal,
          required: false,
          disabled: false,
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          placeholder: 'Is Registered',
          type: FormFieldType.switch,
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.isRegistered)
              ? defaultValues.isRegistered
              : initialValues.isRegistered,
          required: false,
          disabled: isReinsurer,
          // className: customClasses.isRegisteredStyle,
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Register Number',
          placeholder: 'Register Number',
          type: FormFieldType.text,
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.registerNumber)
              ? defaultValues.registerNumber
              : initialValues.registerNumber,
          required: false,
          disabled: false,
        },
        taxAccount: {
          name: 'taxAccount',
          title: 'Tax Account',
          placeholder: 'Tax Account',
          type: FormFieldType.text,
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.taxAccount)
              ? defaultValues.taxAccount
              : initialValues.taxAccount,
          required: true,
          disabled: false,
          customValidator: (values) => {
            const { taxAccount } = values;
            if (taxAccount.length !== 8) {
              return 'Tax Account must have 8 characters';
            }
            return '';
          },
        },
        account: {
          name: 'account',
          title: 'Account',
          placeholder: 'Account',
          type: FormFieldType.text,
          value:
            !isEmpty(defaultValues) && !isEmpty(defaultValues.account)
              ? defaultValues.account
              : initialValues.account,
          required: true,
          disabled: false,
          customValidator: (values) => {
            const { account } = values;
            if (account.length !== 8) {
              return 'Account must have 8 characters';
            }
            return '';
          },
        },
        maxCreditLimitAllowedCurrency: {
          name: 'maxCreditLimitAllowedCurrency',
          title: 'Max Credit Limit Allowed (Currency)',
          type: FormFieldType.select,
          placeholder: 'Max Credit Limit Allowed (Currency)',
          value:
            !isEmpty(defaultValues) &&
            !isEmpty(defaultValues.maxCreditLimitAllowedCurrency)
              ? defaultValues.maxCreditLimitAllowedCurrency
              : initialValues.maxCreditLimitAllowedCurrency,
          disabled: false,
          required: !businessPartnerId,
          hidden: !!businessPartnerId || isReinsurer,
          injectGlobalLov: {
            type: 'currency',
            value: 'Id',
            display: 'Title',
          },
        },
        maxCreditLimitAllowed: {
          name: 'maxCreditLimitAllowed',
          title: 'Max Credit Limit Allowed',
          type: FormFieldType.currency,
          placeholder: 'Max Credit Limit Allowed',
          value:
            !isEmpty(defaultValues) &&
            !isEmpty(defaultValues.maxCreditLimitAllowed)
              ? defaultValues.maxCreditLimitAllowed
              : initialValues.maxCreditLimitAllowed,
          required: !businessPartnerId,
          disabled: false,
          hidden: !!businessPartnerId || isReinsurer,
        },
        SalesforceManagement_BusinessPartnerStatuses: {
          name: 'SalesforceManagement_BusinessPartnerStatuses',
          title: 'Status',
          type: FormFieldType.select,
          value:
            !isEmpty(defaultValues) &&
            !isEmpty(defaultValues.SalesforceManagement_BusinessPartnerStatuses)
              ? defaultValues.SalesforceManagement_BusinessPartnerStatuses
              : initialValues.SalesforceManagement_BusinessPartnerStatuses,
          required: !businessPartnerId,
          disabled: true,
          hidden: !!businessPartnerId,
          selectOptions: lovs.SalesforceManagement_BusinessPartnerStatuses,
        },
      },
    },
  ];
};
